import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, KeyRound, Loader2, Check } from "lucide-react";
import { API } from "@/lib/constants";

export default function LeadMagnet() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { name, email });
      toast.success(data.message || "Dziękujemy!");
      setDone(true);
      setName("");
      setEmail("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Coś poszło nie tak. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="materialy" className="py-20 md:py-32 bg-forest grain relative overflow-hidden">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="text-sm font-outfit uppercase tracking-[0.2em] text-gold">Strefa Premium · Za darmo</span>
          <h2 className="mt-4 font-outfit font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-beige leading-[1.1]" data-testid="leadmagnet-title">
            Pobierz darmowego interaktywnego e-booka
          </h2>
          <p className="mt-5 font-outfit text-xl md:text-2xl text-gold leading-snug" data-testid="leadmagnet-ebook-title">
            NotebookLM: Cyfrowy Warsztat Nowoczesnego Nauczyciela
          </p>
          <p className="mt-5 font-manrope text-base md:text-lg text-beige/80 leading-relaxed max-w-lg">
            Zapisz się do newslettera i odbierz interaktywnego e-booka, który krok po kroku
            pokazuje, jak wykorzystać NotebookLM w codziennej pracy nauczyciela.
          </p>

          <ul className="mt-8 space-y-4">
            {[
              { icon: BookOpen, t: "Interaktywny e-book o NotebookLM", d: "Praktyczny przewodnik z klikalnymi przykładami i gotowymi scenariuszami." },
              { icon: KeyRound, t: "Cyfrowy warsztat nauczyciela", d: "Sprawdzone techniki, które oszczędzają czas na przygotowaniu lekcji." },
            ].map((b) => (
              <li key={b.t} className="flex gap-4">
                <span className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gold/15 text-gold">
                  <b.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <div className="font-outfit text-beige font-medium">{b.t}</div>
                  <div className="font-manrope text-sm text-beige/70 mt-0.5">{b.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="bg-beige rounded-2xl p-8 md:p-10 shadow-clay-lg"
        >
          {done ? (
            <div className="text-center py-10" data-testid="leadmagnet-success">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-forest text-gold mb-5">
                <Check className="h-8 w-8" strokeWidth={2} />
              </span>
              <h3 className="font-outfit text-2xl text-ink font-medium">E-book w drodze!</h3>
              <p className="mt-3 font-manrope text-ink-soft">Sprawdź swoją skrzynkę e-mail (także folder spam).</p>
              <button onClick={() => setDone(false)} className="mt-6 text-sm font-outfit text-forest border-b border-gold pb-0.5" data-testid="leadmagnet-reset">
                Zapisz kolejny adres
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="leadmagnet-form">
              <h3 className="font-outfit text-2xl text-ink font-medium">Pobierz e-booka</h3>
              <p className="mt-2 font-manrope text-sm text-ink-soft">Bez spamu. Wypisujesz się jednym kliknięciem.</p>

              <div className="mt-7 space-y-4">
                <div>
                  <label className="block font-outfit text-sm text-ink mb-1.5">Imię (opcjonalnie)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Np. Anna"
                    className="w-full bg-surface border border-line px-4 py-3 rounded-2xl font-manrope text-ink placeholder:text-ink-soft/50 shadow-clay-inset focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
                    data-testid="leadmagnet-name-input"
                  />
                </div>
                <div>
                  <label className="block font-outfit text-sm text-ink mb-1.5">Adres e-mail</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="twoj@email.pl"
                    className="w-full bg-surface border border-line px-4 py-3 rounded-2xl font-manrope text-ink placeholder:text-ink-soft/50 shadow-clay-inset focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
                    data-testid="leadmagnet-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-gold text-forest px-8 py-4 rounded-2xl font-outfit font-medium tracking-wide hover:bg-gold-hover shadow-clay hover:shadow-clay-lg transition-all duration-300 disabled:opacity-60"
                data-testid="leadmagnet-submit-button"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Pobieram darmowy pakiet"}
              </button>
              <p className="mt-4 text-center font-manrope text-xs text-ink-soft">
                Zapisując się akceptujesz politykę prywatności.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
